/**
 * cesiumPlotHelpers.ts — Full Cesium visualization engine for GenericSimulator
 *
 * Implements all visual elements from the reference architecture:
 *  - Entity positioning (ECEF → Cartesian3)
 *  - Orientation (quaternion + velocity-derived fallback)
 *  - Orbital track (from ECEF state vector: r × v → inclination, RAAN, 360-point)
 *  - FOV cone (sensor half-angle 15°, cylinder)
 *  - FOR cone (horizon half-angle, cylinder)
 *  - Orientation axes (XYZ body frame polylines)
 *  - Trajectory trails (missile/aircraft/vessel accumulated positions)
 *  - Peer-to-peer comm lines (from RepGetLinkStats, earth intersection coloring)
 *  - FOV-based comm lines (satellite–ground, elevation ≥ 10°)
 *  - GEO detection (skip orbit for geostationary satellites)
 *
 * All functions are pure (no hooks) — reusable for live + replay.
 */

import * as Cesium from 'cesium';
import { truncateName } from '../../../utils/commonFunctions';
import {
    ECEFState,
    ElementMap,
    ElementPositionData,
    ElementType,
    LinkStatsData,
    RepElementFixedStatePayload,
    RepGetLinkStatsPayload,
    TrailPoint,
    TRAJECTORY_TYPES,
    VisualizationSettings,
    WSMessage,
} from '../types';

// ═══════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════
export const EARTH_RADIUS = 6_371_000;          // meters
const GEO_ALTITUDE = 35_786_000;                // meters
const GEO_TOLERANCE = 1_000_000;                // meters
const MODEL_DISTANCE_THRESHOLD = 5_000_000;     // meters (camera LOD switch)
const MIN_ELEVATION_DEG = 10;                   // degrees
const FOV_HALF_ANGLE_DEG = 15;                  // degrees
const GS_FOV_CONE_HEIGHT = 500_000;             // meters (500 km)
const SAT_COLOR = '#E6A64E';
const AXIS_LENGTH_SATELLITE = 100_000;          // 100 km

// ── Red triangle billboard image (missile marker) ──
let _missileTriangleCanvas: HTMLCanvasElement | null = null;
const getMissileTriangleImage = (): HTMLCanvasElement => {
    if (_missileTriangleCanvas) return _missileTriangleCanvas;
    const size = 32;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    // Upward-pointing triangle (rotation handled by billboard.rotation)
    ctx.beginPath();
    ctx.moveTo(size / 2, 2);          // top center
    ctx.lineTo(size - 2, size - 2);   // bottom right
    ctx.lineTo(2, size - 2);          // bottom left
    ctx.closePath();
    ctx.fillStyle = '#FF4444';
    ctx.fill();
    ctx.strokeStyle = '#FF8888';
    ctx.lineWidth = 1;
    ctx.stroke();
    _missileTriangleCanvas = canvas;
    return canvas;
};

// ── Orbit recalculation tracking ──
// Stores the last angular-momentum vector (h = r × v) per element.
// When h changes beyond a threshold, the orbit polyline is regenerated.
const _lastOrbitH: Map<string, { hx: number; hy: number; hz: number }> = new Map();
const ORBIT_H_CHANGE_THRESHOLD = 0.01; // ~0.6° change in orbital plane triggers recalc

// ── Live position/orientation cache (shared between entity + cones) ──
// FOV/FOR cone entities read from this cache via CallbackProperty so they
// are always in lock-step with the parent element on every render frame.
interface ElementPoseCache {
    position: Cesium.Cartesian3;
    orientation: Cesium.Quaternion;
    elementType: ElementType;
}
const _poseCache: Map<string, ElementPoseCache> = new Map();

// ── Trail positions cache (avoids recreating polyline positions every frame) ──
// Trail polyline entities read from this cache via CallbackProperty so they
// update smoothly without flickering.
const _trailCache: Map<string, Cesium.Cartesian3[]> = new Map();

// ── Module-level trail raw data (avoids React state updates per WS message) ──
const _trailDataCache: Map<string, TrailPoint[]> = new Map();

// ── Module-level ECEF history (avoids React state updates per WS message) ──
const _ecefHistoryCache: Map<string, ECEFState[]> = new Map();

// ── Viewer guard — reusable check for null / destroyed viewer ──
export const isViewerReady = (viewer: Cesium.Viewer | null | undefined): viewer is Cesium.Viewer =>
    !!viewer && !viewer.isDestroyed();

// ── Render throttle — batches multiple requestRender calls in a single frame ──
let _renderRequested = false;
export const throttledRequestRender = (viewer: Cesium.Viewer): void => {
    if (_renderRequested) return;
    _renderRequested = true;
    requestAnimationFrame(() => {
        _renderRequested = false;
        if (isViewerReady(viewer)) {
            viewer.scene.requestRender();
        }
    });
};

/** Compute heading (radians, 0=north, CW positive) from ECEF velocity at a position. */
const computeHeadingFromVelocity = (
    position: Cesium.Cartesian3,
    velocity: Cesium.Cartesian3,
): number => {
    const velMag = Cesium.Cartesian3.magnitude(velocity);
    if (velMag < 0.1) return 0;

    // Transform to local ENU
    const transform = Cesium.Transforms.eastNorthUpToFixedFrame(position);
    const inverse = Cesium.Matrix4.inverse(transform, new Cesium.Matrix4());
    const localVel = Cesium.Matrix4.multiplyByPointAsVector(inverse, velocity, new Cesium.Cartesian3());
    // ENU: x=east, y=north, z=up → heading = atan2(east, north)
    return Math.atan2(localVel.x, localVel.y);
};
const AXIS_LENGTH_TRAJECTORY = 50_000;          // 50 km

// ═══════════════════════════════════════════════════════════════════════
// ELEMENT MAP BUILDER
// ═══════════════════════════════════════════════════════════════════════

/**
 * Infer element type from its name (fallback heuristic).
 * Checks common keywords in the name string.
 */
export const inferElementTypeFromName = (name: string): ElementType => {
    if (!name) return 'unknown';
    const lower = name.toLowerCase();
    // Missiles / threats / interceptors
    if (lower.includes('missile') || lower.includes('interceptor') || lower.includes('projectile')
        || lower.includes('rocket') || lower.includes('weapon') || lower.includes('threat')
        || lower.includes('tbm') || lower.includes('icbm') || lower.includes('irbm')
        || lower.includes('srbm') || lower.includes('warhead') || lower.includes('target')
        || lower.includes('mrbm') || lower.includes('ballistic') || lower.includes('decoy')
        || lower.includes('kill_vehicle') || lower.includes('kv_') || lower.includes('kinetic')
    ) return 'missile';
    if (lower.includes('aircraft') || lower.includes('plane') || lower.includes('uav') || lower.includes('drone')) return 'aircraft';
    if (lower.includes('vessel') || lower.includes('ship') || lower.includes('boat')) return 'vessel';
    if (lower.includes('ground') || lower.includes('station') || lower.includes('gateway') || lower.includes('terminal') || lower.includes('gs_') || lower.includes('gs-')) return 'groundstation';
    if (lower.includes('satellite') || lower.includes('sat_') || lower.includes('sat-')) return 'satellite';
    return 'unknown';
};

export const buildElementMapFromDeployment = (deploymentData: any): ElementMap => {
    const elementMap: ElementMap = {};
    if (!deploymentData) return elementMap;

    // Unwrap nested response structures — API may return { data: { ... } }
    let src = deploymentData;
    if (!src.satelliteGroups && !src.groundStations && !src.elements && src.data) {
        src = src.data;
    }

    // ── Flat elements array (new API format) ──
    // { elements: [{ elementId, elementName, elementType, trueTwinId, ... }] }
    const flatElements: any[] = src?.elements || [];
    if (flatElements.length > 0) {
        for (const el of flatElements) {
            const id = el?.trueTwinId || el?.truetwinId || el?.elementId;
            if (!id) continue;
            const rawType = (el?.elementType || '').toLowerCase();
            const type: ElementType =
                rawType === 'satellite'     ? 'satellite' :
                rawType === 'groundstation' ? 'groundstation' :
                rawType === 'missile'       ? 'missile' :
                rawType === 'aircraft'      ? 'aircraft' :
                rawType === 'vessel'        ? 'vessel' :
                inferElementTypeFromName(el?.elementName || '');
            elementMap[id] = {
                elementId: id,
                elementName: el?.elementName || el?.name || 'Unknown',
                elementType: type,
            };
        }
        return elementMap;
    }

    // ── Satellites (legacy grouped format) ──
    const satelliteGroups = src?.satelliteGroups || [];
    for (const group of satelliteGroups) {
        const satellites = group?.satellites || [];
        const groupOrbit = group?.groupReferenceOrbit || group?.referenceOrbitParameters || null;

        for (const sat of satellites) {
            const truetwinId = sat?.truetwinId || sat?.trueTwinId;
            if (truetwinId) {
                elementMap[truetwinId] = {
                    elementId: truetwinId,
                    elementName: sat?.satelliteName || 'Unknown Satellite',
                    elementType: 'satellite',
                    groupColor: group?.color || SAT_COLOR,
                    groupName: group?.satelliteGroupName || '',
                    satelliteId: sat?.satelliteId,
                    orbitParameters: sat?.orbitParameters || groupOrbit || undefined,
                };
            }
        }
    }

    // ── Ground Stations ──
    const groundStations = src?.groundStations || src?.ground_stations || [];
    for (const gs of groundStations) {
        const gsId = gs?.truetwinId || gs?.trueTwinId || gs?.groundStationId;
        if (gsId) {
            elementMap[gsId] = {
                elementId: gsId,
                elementName: gs?.groundStationName || gs?.name || 'Ground Station',
                elementType: 'groundstation',
                location: gs?.location || gs?.position || undefined,
            };
        }
    }

    // ── Missiles / Interceptors ──
    const missiles = src?.missiles || src?.interceptors || src?.threats || [];
    for (const m of missiles) {
        const mId = m?.truetwinId || m?.trueTwinId || m?.missileId || m?.id;
        if (mId) {
            elementMap[mId] = {
                elementId: mId,
                elementName: m?.missileName || m?.name || 'Missile',
                elementType: 'missile',
            };
        }
    }

    // ── Aircraft ──
    const aircraft = src?.aircraft || src?.aircrafts || [];
    for (const a of aircraft) {
        const aId = a?.truetwinId || a?.trueTwinId || a?.aircraftId || a?.id;
        if (aId) {
            elementMap[aId] = {
                elementId: aId,
                elementName: a?.aircraftName || a?.name || 'Aircraft',
                elementType: 'aircraft',
            };
        }
    }

    // ── Vessels ──
    const vessels = src?.vessels || src?.ships || [];
    for (const v of vessels) {
        const vId = v?.truetwinId || v?.trueTwinId || v?.vesselId || v?.id;
        if (vId) {
            elementMap[vId] = {
                elementId: vId,
                elementName: v?.vesselName || v?.name || 'Vessel',
                elementType: 'vessel',
            };
        }
    }

    return elementMap;
};

// ═══════════════════════════════════════════════════════════════════════
// POSITION PARSER
// ═══════════════════════════════════════════════════════════════════════

export const parsePositionFromPayload = (msg: WSMessage): ElementPositionData | null => {
    if (msg.commandName !== 'RepElementFixedState') return null;
    const p = msg.payload as RepElementFixedStatePayload;
    return {
        elementId: msg.elementId,
        elementName: msg.elementName,
        timestamp: msg.timestamp,
        position: { x: p.RepElementFixedState__pos_x, y: p.RepElementFixedState__pos_y, z: p.RepElementFixedState__pos_z },
        velocity: { x: p.RepElementFixedState__vel_x, y: p.RepElementFixedState__vel_y, z: p.RepElementFixedState__vel_z },
        quaternion: { q1: p.RepElementFixedState__q_1, q2: p.RepElementFixedState__q_2, q3: p.RepElementFixedState__q_3, q4: p.RepElementFixedState__q_4 },
    };
};

export const parseLinkStatsFromPayload = (msg: WSMessage): LinkStatsData | null => {
    if (msg.commandName !== 'RepGetLinkStats') return null;
    const p = msg.payload as RepGetLinkStatsPayload;
    return {
        elementId: msg.elementId,
        activePeers: p.RepGetLinkStats__active_peers?.filter(id => id !== 0) || [],
        timestamp: msg.timestamp,
    };
};

export const isFixedElement = (data: ElementPositionData): boolean =>
    data.velocity.x === 0 && data.velocity.y === 0 && data.velocity.z === 0;

export const resolveElementType = (
    data: ElementPositionData,
    elementMap: ElementMap,
): ElementType => {
    const info = elementMap[data.elementId];
    if (info && info.elementType !== 'unknown') return info.elementType;
    if (isFixedElement(data)) return 'groundstation';

    // Try to infer from element name (e.g., "Missile_1" → missile)
    const inferred = inferElementTypeFromName(data.elementName);
    if (inferred !== 'unknown') return inferred;

    // Fallback: moving elements without any recognizable name → satellite
    return 'satellite';
};

// ═══════════════════════════════════════════════════════════════════════
// § 5 — ECEF TO CESIUM (Direct Cartesian3)
// ═══════════════════════════════════════════════════════════════════════

const ecefToCartesian3 = (x: number, y: number, z: number): Cesium.Cartesian3 =>
    new Cesium.Cartesian3(x, y, z);

// ═══════════════════════════════════════════════════════════════════════
// § 6 — ORIENTATION & ATTITUDE
// ═══════════════════════════════════════════════════════════════════════

/**
 * Compute orientation from telemetry quaternion or velocity-derived fallback.
 */
export const calculateModelOrientation = (
    data: ElementPositionData,
    elementType: ElementType,
): Cesium.Quaternion => {
    const { quaternion, position, velocity } = data;

    // Priority: telemetry quaternion
    if (quaternion.q4 !== 0 || quaternion.q1 !== 0 || quaternion.q2 !== 0 || quaternion.q3 !== 0) {
        return new Cesium.Quaternion(quaternion.q1, quaternion.q2, quaternion.q3, quaternion.q4);
    }

    // Fallback: velocity-derived
    const pos = new Cesium.Cartesian3(position.x, position.y, position.z);
    const vel = new Cesium.Cartesian3(velocity.x, velocity.y, velocity.z);
    const velMag = Cesium.Cartesian3.magnitude(vel);

    if (velMag < 0.1) {
        // Too slow — use surface-aligned default
        const hpr = new Cesium.HeadingPitchRoll(0, 0, 0);
        return Cesium.Transforms.headingPitchRollQuaternion(pos, hpr);
    }

    const radialUp = Cesium.Cartesian3.normalize(pos, new Cesium.Cartesian3());
    const velocityDir = Cesium.Cartesian3.normalize(vel, new Cesium.Cartesian3());

    let xAxis: Cesium.Cartesian3, yAxis: Cesium.Cartesian3, zAxis: Cesium.Cartesian3;

    if (elementType === 'satellite') {
        // Y = velocity (along-track), Z = up (radial), X = cross-track
        yAxis = velocityDir;
        xAxis = Cesium.Cartesian3.cross(yAxis, radialUp, new Cesium.Cartesian3());
        Cesium.Cartesian3.normalize(xAxis, xAxis);
        Cesium.Cartesian3.negate(xAxis, xAxis);
        zAxis = Cesium.Cartesian3.cross(xAxis, yAxis, new Cesium.Cartesian3());
        Cesium.Cartesian3.normalize(zAxis, zAxis);
    } else {
        // Trajectory: X = velocity (forward), Z = up, Y = right
        xAxis = velocityDir;
        yAxis = Cesium.Cartesian3.cross(radialUp, xAxis, new Cesium.Cartesian3());
        Cesium.Cartesian3.normalize(yAxis, yAxis);
        zAxis = Cesium.Cartesian3.cross(xAxis, yAxis, new Cesium.Cartesian3());
        Cesium.Cartesian3.normalize(zAxis, zAxis);
    }

    const rotMatrix = new Cesium.Matrix3(
        xAxis.x, yAxis.x, zAxis.x,
        xAxis.y, yAxis.y, zAxis.y,
        xAxis.z, yAxis.z, zAxis.z,
    );
    return Cesium.Quaternion.fromRotationMatrix(rotMatrix);
};

// ═══════════════════════════════════════════════════════════════════════
// § 8 — ORBITAL TRACK (from ECEF state vector r × v)
// ═══════════════════════════════════════════════════════════════════════

/**
 * Compute orbital elements from position + velocity, then generate 360 points.
 * Matches the reference: h = r × v, inclination = acos(hz/|h|), RAAN from node vector.
 */
export const computeOrbitalElements = (
    pos: { x: number; y: number; z: number },
    vel: { x: number; y: number; z: number },
): { rMag: number; inclination: number; RAAN: number } | null => {
    const rMag = Math.sqrt(pos.x ** 2 + pos.y ** 2 + pos.z ** 2);
    if (rMag < 1000) return null; // too close to origin

    // Specific angular momentum: h = r × v
    const hx = pos.y * vel.z - pos.z * vel.y;
    const hy = pos.z * vel.x - pos.x * vel.z;
    const hz = pos.x * vel.y - pos.y * vel.x;
    const hMag = Math.sqrt(hx ** 2 + hy ** 2 + hz ** 2);
    if (hMag < 1e-10) return null;

    // Inclination
    const cosI = Math.max(-1, Math.min(1, hz / hMag));
    const inclination = Math.acos(cosI);

    // Node vector: n = k̂ × h = (-hy, hx, 0)
    const nx = -hy;
    const ny = hx;
    const nMag = Math.sqrt(nx ** 2 + ny ** 2);

    let RAAN = 0;
    if (nMag > 1e-10) {
        RAAN = Math.acos(Math.max(-1, Math.min(1, nx / nMag)));
        if (ny < 0) RAAN = 2 * Math.PI - RAAN;
    }

    return { rMag, inclination, RAAN };
};

/**
 * Generate 360-point orbit path from computed elements.
 * Uses current orbital radius as circular approximation.
 */
export const generateOrbitalTrackFromStateVector = (
    pos: { x: number; y: number; z: number },
    vel: { x: number; y: number; z: number },
    numPoints: number = 360,
): Cesium.Cartesian3[] | null => {
    const elements = computeOrbitalElements(pos, vel);
    if (!elements) return null;

    const { rMag, inclination, RAAN } = elements;
    const pts: Cesium.Cartesian3[] = [];

    for (let k = 0; k < numPoints; k++) {
        const theta = (k / numPoints) * 2 * Math.PI;

        // Point in orbital plane
        const xOrb = rMag * Math.cos(theta);
        const yOrb = rMag * Math.sin(theta);

        // Rotate by inclination (around X-axis)
        const xRot = xOrb;
        const yRot = yOrb * Math.cos(inclination);
        const zRot = yOrb * Math.sin(inclination);

        // Rotate by RAAN (around Z-axis)
        const xFinal = xRot * Math.cos(RAAN) - yRot * Math.sin(RAAN);
        const yFinal = xRot * Math.sin(RAAN) + yRot * Math.cos(RAAN);
        const zFinal = zRot;

        pts.push(new Cesium.Cartesian3(xFinal, yFinal, zFinal));
    }

    pts.push(pts[0]); // close loop
    return pts;
};

// ═══════════════════════════════════════════════════════════════════════
// § 17 — GEO DETECTION
// ═══════════════════════════════════════════════════════════════════════

export const isGEO = (pos: { x: number; y: number; z: number }): boolean => {
    const rMag = Math.sqrt(pos.x ** 2 + pos.y ** 2 + pos.z ** 2);
    const altitude = rMag - EARTH_RADIUS;
    return Math.abs(altitude - GEO_ALTITUDE) <= GEO_TOLERANCE;
};

// ═══════════════════════════════════════════════════════════════════════
// § 9 — FOV CONE (uses CallbackProperty → locked to parent element)
// ═══════════════════════════════════════════════════════════════════════

/**
 * Helper: compute satellite FOV cone center + dimensions from cached pose.
 */
const computeSatFOVGeometry = (cache: ElementPoseCache) => {
    const rMag = Cesium.Cartesian3.magnitude(cache.position);
    const altitude = rMag - EARTH_RADIUS;
    if (altitude <= 0) return null;

    const halfAngleRad = Cesium.Math.toRadians(FOV_HALF_ANGLE_DEG);
    const coneLength = altitude;
    const wideRadius = coneLength * Math.tan(halfAngleRad);

    const rotMatrix = Cesium.Matrix3.fromQuaternion(cache.orientation);
    const zAxis = Cesium.Matrix3.getColumn(rotMatrix, 2, new Cesium.Cartesian3());
    Cesium.Cartesian3.normalize(zAxis, zAxis);

    const coneCenter = new Cesium.Cartesian3();
    Cesium.Cartesian3.multiplyByScalar(zAxis, coneLength / 2, coneCenter);
    Cesium.Cartesian3.add(cache.position, coneCenter, coneCenter);

    return { coneCenter, coneLength, wideRadius };
};

// Per-frame cache so multiple CallbackProperties sharing the same element
// don't recompute geometry redundantly within the same render frame.
let _fovFrameCache: Map<string, ReturnType<typeof computeSatFOVGeometry>> = new Map();
let _forFrameCache: Map<string, ReturnType<typeof computeSatFORGeometry>> = new Map();
let _lastFrameTime = 0;

const getFrameCaches = () => {
    const now = performance.now();
    // Clear per-frame caches at start of a new frame (~16ms threshold)
    if (now - _lastFrameTime > 2) {
        _fovFrameCache = new Map();
        _forFrameCache = new Map();
        _lastFrameTime = now;
    }
};

const getCachedFOVGeometry = (elementId: string): ReturnType<typeof computeSatFOVGeometry> => {
    getFrameCaches();
    let g = _fovFrameCache.get(elementId);
    if (g !== undefined) return g;
    const c = _poseCache.get(elementId);
    g = c ? computeSatFOVGeometry(c) : null;
    _fovFrameCache.set(elementId, g);
    return g;
};

const getCachedFORGeometry = (elementId: string): ReturnType<typeof computeSatFORGeometry> => {
    getFrameCaches();
    let g = _forFrameCache.get(elementId);
    if (g !== undefined) return g;
    const c = _poseCache.get(elementId);
    g = c ? computeSatFORGeometry(c) : null;
    _forFrameCache.set(elementId, g);
    return g;
};

export const createFOVCone = (
    viewer: Cesium.Viewer,
    elementId: string,
    elementName: string,
    position: Cesium.Cartesian3,
    orientation: Cesium.Quaternion,
    elementType: ElementType,
): void => {
    const fovId = `${elementId}-fov`;
    if (viewer.entities.getById(fovId)) return;

    const halfAngleRad = Cesium.Math.toRadians(FOV_HALF_ANGLE_DEG);

    if (elementType === 'satellite') {
        const rMag = Cesium.Cartesian3.magnitude(position);
        const altitude = rMag - EARTH_RADIUS;
        if (altitude <= 0) return;

        const coneLength = altitude;
        const wideRadius = coneLength * Math.tan(halfAngleRad);

        // Initial geometry
        const rotMatrix = Cesium.Matrix3.fromQuaternion(orientation);
        const zAxis = Cesium.Matrix3.getColumn(rotMatrix, 2, new Cesium.Cartesian3());
        Cesium.Cartesian3.normalize(zAxis, zAxis);

        const coneCenter = new Cesium.Cartesian3();
        Cesium.Cartesian3.multiplyByScalar(zAxis, coneLength / 2, coneCenter);
        Cesium.Cartesian3.add(position, coneCenter, coneCenter);

        viewer.entities.add({
            id: fovId,
            name: `${elementName} FOV`,
            // CallbackProperty reads from _poseCache every frame → zero lag
            position: new Cesium.CallbackProperty(() => {
                const g = getCachedFOVGeometry(elementId);
                return g ? g.coneCenter : coneCenter;
            }, false) as any,
            orientation: new Cesium.CallbackProperty(() => {
                const c = _poseCache.get(elementId);
                return c ? c.orientation : orientation;
            }, false) as any,
            cylinder: {
                length: new Cesium.CallbackProperty(() => {
                    const g = getCachedFOVGeometry(elementId);
                    return g ? g.coneLength : coneLength;
                }, false) as any,
                topRadius: new Cesium.CallbackProperty(() => {
                    const g = getCachedFOVGeometry(elementId);
                    return g ? g.wideRadius : wideRadius;
                }, false) as any,
                bottomRadius: 0,
                material: Cesium.Color.fromCssColorString(SAT_COLOR).withAlpha(0.15),
                outline: true,
                outlineColor: Cesium.Color.fromCssColorString(SAT_COLOR).withAlpha(0.5),
                numberOfVerticalLines: 16,
            },
        });
    } else if (elementType === 'groundstation') {
        const coneLength = GS_FOV_CONE_HEIGHT;
        const wideRadius = coneLength * Math.tan(halfAngleRad);

        const upDir = Cesium.Cartesian3.normalize(position, new Cesium.Cartesian3());
        const coneCenter = new Cesium.Cartesian3();
        Cesium.Cartesian3.multiplyByScalar(upDir, coneLength / 2, coneCenter);
        Cesium.Cartesian3.add(position, coneCenter, coneCenter);

        const hpr = new Cesium.HeadingPitchRoll(0, 0, 0);
        const gsOrientation = Cesium.Transforms.headingPitchRollQuaternion(position, hpr);

        viewer.entities.add({
            id: fovId,
            name: `${elementName} FOV`,
            position: coneCenter,
            orientation: gsOrientation as any,
            cylinder: {
                length: coneLength,
                topRadius: wideRadius,
                bottomRadius: 0,
                material: Cesium.Color.fromCssColorString(SAT_COLOR).withAlpha(0.15),
                outline: true,
                outlineColor: Cesium.Color.fromCssColorString(SAT_COLOR).withAlpha(0.5),
                numberOfVerticalLines: 16,
            },
        });
    }
};

export const updateFOVCone = (
    _viewer: Cesium.Viewer,
    _elementId: string,
    _position: Cesium.Cartesian3,
    _orientation: Cesium.Quaternion,
    _elementType: ElementType,
): void => {
    // For satellites: CallbackProperty reads from _poseCache automatically,
    // so no manual update is needed — everything moves in lock-step.
    // Ground stations are static, so no update needed either.
};

// ═══════════════════════════════════════════════════════════════════════
// § 10 — FOR CONE (Field of Regard — CallbackProperty → locked to parent)
// ═══════════════════════════════════════════════════════════════════════

/**
 * Helper: compute satellite FOR cone center + dimensions from cached pose.
 */
const computeSatFORGeometry = (cache: ElementPoseCache) => {
    const rMag = Cesium.Cartesian3.magnitude(cache.position);
    const altitude = rMag - EARTH_RADIUS;
    if (altitude <= 0) return null;

    const forHalfAngle = Math.asin(EARTH_RADIUS / (EARTH_RADIUS + altitude));
    const coneLength = altitude;
    const baseRadius = coneLength * Math.tan(forHalfAngle);

    const radialDir = Cesium.Cartesian3.normalize(cache.position, new Cesium.Cartesian3());
    const coneCenter = new Cesium.Cartesian3();
    Cesium.Cartesian3.multiplyByScalar(radialDir, -coneLength / 2, coneCenter);
    Cesium.Cartesian3.add(cache.position, coneCenter, coneCenter);

    const hpr = new Cesium.HeadingPitchRoll(0, 0, 0);
    const forOrientation = Cesium.Transforms.headingPitchRollQuaternion(cache.position, hpr);

    // bottomRadius = wide base (toward Earth), topRadius = 0 tip (at satellite)
    return { coneCenter, coneLength, bottomRadius: baseRadius, topRadius: 0, forOrientation };
};

export const createFORCone = (
    viewer: Cesium.Viewer,
    elementId: string,
    elementName: string,
    position: Cesium.Cartesian3,
    elementType: ElementType,
): void => {
    const forId = `${elementId}-for`;
    if (viewer.entities.getById(forId)) return;

    if (elementType === 'satellite') {
        const rMag = Cesium.Cartesian3.magnitude(position);
        const altitude = rMag - EARTH_RADIUS;
        if (altitude <= 0) return;

        const forHalfAngle = Math.asin(EARTH_RADIUS / (EARTH_RADIUS + altitude));
        const coneLength = altitude;
        const baseRadius = coneLength * Math.tan(forHalfAngle);

        const radialDir = Cesium.Cartesian3.normalize(position, new Cesium.Cartesian3());
        const coneCenter = new Cesium.Cartesian3();
        Cesium.Cartesian3.multiplyByScalar(radialDir, -coneLength / 2, coneCenter);
        Cesium.Cartesian3.add(position, coneCenter, coneCenter);

        const hpr = new Cesium.HeadingPitchRoll(0, 0, 0);
        const forOrientation = Cesium.Transforms.headingPitchRollQuaternion(position, hpr);

        viewer.entities.add({
            id: forId,
            name: `${elementName} FOR`,
            // CallbackProperty reads from _poseCache every frame → zero lag
            position: new Cesium.CallbackProperty(() => {
                const g = getCachedFORGeometry(elementId);
                return g ? g.coneCenter : coneCenter;
            }, false) as any,
            orientation: new Cesium.CallbackProperty(() => {
                const g = getCachedFORGeometry(elementId);
                return g ? g.forOrientation : forOrientation;
            }, false) as any,
            cylinder: {
                length: new Cesium.CallbackProperty(() => {
                    const g = getCachedFORGeometry(elementId);
                    return g ? g.coneLength : coneLength;
                }, false) as any,
                topRadius: 0,
                bottomRadius: new Cesium.CallbackProperty(() => {
                    const g = getCachedFORGeometry(elementId);
                    return g ? g.bottomRadius : baseRadius;
                }, false) as any,
                material: Cesium.Color.DEEPSKYBLUE.withAlpha(0.08),
                outline: true,
                outlineColor: Cesium.Color.DEEPSKYBLUE.withAlpha(0.3),
                numberOfVerticalLines: 32,
            },
        });
    } else if (elementType === 'groundstation') {
        const coneLength = GS_FOV_CONE_HEIGHT;
        const forHalfAngle = Cesium.Math.toRadians(90 - MIN_ELEVATION_DEG);
        const topRadius = coneLength * Math.tan(forHalfAngle);

        const upDir = Cesium.Cartesian3.normalize(position, new Cesium.Cartesian3());
        const coneCenter = new Cesium.Cartesian3();
        Cesium.Cartesian3.multiplyByScalar(upDir, coneLength / 2, coneCenter);
        Cesium.Cartesian3.add(position, coneCenter, coneCenter);

        const hpr = new Cesium.HeadingPitchRoll(0, 0, 0);
        const gsOrientation = Cesium.Transforms.headingPitchRollQuaternion(position, hpr);

        viewer.entities.add({
            id: forId,
            name: `${elementName} FOR`,
            position: coneCenter,
            orientation: gsOrientation as any,
            cylinder: {
                length: coneLength,
                topRadius: topRadius,
                bottomRadius: 0,
                material: Cesium.Color.DEEPSKYBLUE.withAlpha(0.08),
                outline: true,
                outlineColor: Cesium.Color.DEEPSKYBLUE.withAlpha(0.3),
                numberOfVerticalLines: 32,
            },
        });
    }
};

export const updateFORCone = (
    _viewer: Cesium.Viewer,
    _elementId: string,
    _position: Cesium.Cartesian3,
    _elementType: ElementType,
): void => {
    // For satellites: CallbackProperty reads from _poseCache automatically,
    // so no manual update is needed — everything moves in lock-step.
    // Ground stations are static, so no update needed either.
};

// ═══════════════════════════════════════════════════════════════════════
// § 11 — ORIENTATION AXES (XYZ Body Frame)
// ═══════════════════════════════════════════════════════════════════════

export const updateOrientationAxes = (
    viewer: Cesium.Viewer,
    elementId: string,
    _position: Cesium.Cartesian3,
    _orientation: Cesium.Quaternion,
    elementType: ElementType,
): void => {
    const axisLength = elementType === 'satellite' ? AXIS_LENGTH_SATELLITE : AXIS_LENGTH_TRAJECTORY;

    const axes: Array<{ suffix: string; col: number; color: Cesium.Color }> = [
        { suffix: 'axis-x', col: 0, color: Cesium.Color.RED },
        { suffix: 'axis-y', col: 1, color: Cesium.Color.GREEN },
        { suffix: 'axis-z', col: 2, color: Cesium.Color.BLUE },
    ];

    for (const { suffix, col, color } of axes) {
        const axisId = `${elementId}-${suffix}`;
        const entity = viewer.entities.getById(axisId);

        // Build a CallbackProperty-based positions array that reads from _poseCache
        const makePositionsCb = () => new Cesium.CallbackProperty(() => {
            const c = _poseCache.get(elementId);
            if (!c) return [_position, _position];
            const rotMatrix = Cesium.Matrix3.fromQuaternion(c.orientation);
            const axisDir = Cesium.Matrix3.getColumn(rotMatrix, col, new Cesium.Cartesian3());
            Cesium.Cartesian3.normalize(axisDir, axisDir);
            const endPoint = new Cesium.Cartesian3();
            Cesium.Cartesian3.multiplyByScalar(axisDir, axisLength, endPoint);
            Cesium.Cartesian3.add(c.position, endPoint, endPoint);
            return [c.position, endPoint];
        }, false);

        if (entity && entity.polyline) {
            // Already using CallbackProperty — nothing to do
            if (!(entity.polyline.positions instanceof Cesium.CallbackProperty)) {
                entity.polyline.positions = makePositionsCb() as any;
            }
        } else if (!entity) {
            viewer.entities.add({
                id: axisId,
                polyline: {
                    positions: makePositionsCb() as any,
                    width: 2,
                    material: color,
                    arcType: Cesium.ArcType.NONE,
                },
            });
        }
    }
};

export const removeOrientationAxes = (viewer: Cesium.Viewer, elementId: string): void => {
    for (const suffix of ['axis-x', 'axis-y', 'axis-z']) {
        viewer.entities.removeById(`${elementId}-${suffix}`);
    }
};

// ═══════════════════════════════════════════════════════════════════════
// § 12 — TRAJECTORY TRAILS (Missiles, Aircraft, Vessels)
// ═══════════════════════════════════════════════════════════════════════

export const updateTrajectoryTrail = (
    viewer: Cesium.Viewer,
    elementId: string,
    elementName: string,
    elementType: ElementType,
    trailPositions: TrailPoint[],
): void => {
    if (trailPositions.length < 2) return;

    const trailId = `${elementId}-trail`;
    // Update the module-level cache — CallbackProperty reads from here
    _trailCache.set(elementId, trailPositions.map(p => new Cesium.Cartesian3(p.x, p.y, p.z)));

    const trailColor = elementType === 'missile' ? Cesium.Color.RED : Cesium.Color.DODGERBLUE;

    const entity = viewer.entities.getById(trailId);
    if (entity) {
        // Entity already exists with a CallbackProperty — just update cache (done above)
        entity.show = true;
    } else {
        // Create entity once with CallbackProperty that reads from _trailCache
        viewer.entities.add({
            id: trailId,
            name: `${elementName} Trail`,
            polyline: {
                positions: new Cesium.CallbackProperty(() => {
                    return _trailCache.get(elementId) || [];
                }, false),
                width: 3,
                material: new Cesium.PolylineGlowMaterialProperty({
                    glowPower: 0.25,
                    color: trailColor,
                }),
                arcType: Cesium.ArcType.NONE,
            },
        });
    }
};

// ═══════════════════════════════════════════════════════════════════════
// § 15 — EARTH INTERSECTION TEST (Ray-Sphere)
// ═══════════════════════════════════════════════════════════════════════

export const doesLineIntersectEarth = (
    p1: Cesium.Cartesian3,
    p2: Cesium.Cartesian3,
): boolean => {
    const R = EARTH_RADIUS;
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const dz = p2.z - p1.z;

    const a = dx * dx + dy * dy + dz * dz;
    const b = 2 * (p1.x * dx + p1.y * dy + p1.z * dz);
    const c = p1.x * p1.x + p1.y * p1.y + p1.z * p1.z - R * R;

    const discriminant = b * b - 4 * a * c;
    if (discriminant < 0) return false;

    const sqrtD = Math.sqrt(discriminant);
    const t1 = (-b - sqrtD) / (2 * a);
    const t2 = (-b + sqrtD) / (2 * a);

    return (t1 >= 0 && t1 <= 1) || (t2 >= 0 && t2 <= 1);
};

// ═══════════════════════════════════════════════════════════════════════
// § 16 — ELEVATION ANGLE
// ═══════════════════════════════════════════════════════════════════════

export const calculateElevationAngle = (
    gsPos: Cesium.Cartesian3,
    satPos: Cesium.Cartesian3,
): number => {
    const delta = new Cesium.Cartesian3();
    Cesium.Cartesian3.subtract(satPos, gsPos, delta);
    const deltaMag = Cesium.Cartesian3.magnitude(delta);
    if (deltaMag < 1) return 0;

    const rGs = Cesium.Cartesian3.magnitude(gsPos);
    const upUnit = Cesium.Cartesian3.divideByScalar(gsPos, rGs, new Cesium.Cartesian3());
    const dotProduct = Cesium.Cartesian3.dot(delta, upUnit);

    return Math.asin(dotProduct / deltaMag); // radians
};

// ═══════════════════════════════════════════════════════════════════════
// § 13 — PEER-TO-PEER COMMUNICATION LINES
// ═══════════════════════════════════════════════════════════════════════

export const updatePeerCommLines = (
    viewer: Cesium.Viewer,
    linkStatsMap: Record<string, LinkStatsData>,
    elementMap: ElementMap,
    nodeIdToElementId: Record<number, string>,
    visSettings: VisualizationSettings,
): void => {
    // Build element position map
    const positionMap: Record<string, Cesium.Cartesian3> = {};
    for (const [elementId] of Object.entries(elementMap)) {
        const entity = viewer.entities.getById(elementId);
        if (entity && entity.position) {
            const pos = entity.position.getValue(viewer.clock.currentTime);
            if (pos && Cesium.Cartesian3.magnitude(pos) > 1000) {
                positionMap[elementId] = pos;
            }
        }
    }

    // Track current active pairs
    const activePairs = new Set<string>();

    for (const [elementId, linkStats] of Object.entries(linkStatsMap)) {
        const sourcePos = positionMap[elementId];
        if (!sourcePos) continue;

        const sourceInfo = elementMap[elementId];
        if (!sourceInfo) continue;

        // Check visibility
        if (!isFeatureVisible(visSettings.commLines, elementId)) continue;
        if (!isFeatureVisible(visSettings.elementVisibility, elementId)) continue;

        for (const peerNodeId of linkStats.activePeers) {
            if (peerNodeId === 0) continue;

            const peerElementId = nodeIdToElementId[peerNodeId];
            if (!peerElementId) continue;

            const peerPos = positionMap[peerElementId];
            if (!peerPos) continue;

            if (!isFeatureVisible(visSettings.commLines, peerElementId)) continue;
            if (!isFeatureVisible(visSettings.elementVisibility, peerElementId)) continue;

            // Sorted pair to prevent duplicates
            const sortedIds = [elementId, peerElementId].sort();
            const commId = `peer-comm-${sortedIds[0]}-${sortedIds[1]}`;
            activePairs.add(commId);

            // Skip connections that are occluded by Earth
            if (doesLineIntersectEarth(sourcePos, peerPos)) continue;
            const lineColor = Cesium.Color.YELLOW;

            // Determine arc type
            const sourceType = elementMap[elementId]?.elementType;
            const peerType = elementMap[peerElementId]?.elementType;
            const bothGround = sourceType === 'groundstation' && peerType === 'groundstation';

            // Elevate ground positions slightly
            let p1 = sourcePos, p2 = peerPos;
            if (bothGround) {
                const u1 = Cesium.Cartesian3.normalize(sourcePos, new Cesium.Cartesian3());
                p1 = Cesium.Cartesian3.add(sourcePos, u1, new Cesium.Cartesian3());
                const u2 = Cesium.Cartesian3.normalize(peerPos, new Cesium.Cartesian3());
                p2 = Cesium.Cartesian3.add(peerPos, u2, new Cesium.Cartesian3());
            }

            const entity = viewer.entities.getById(commId);
            if (entity && entity.polyline) {
                // Use CallbackProperty so comm lines move with elements during replay
                const srcId = elementId;
                const dstId = peerElementId;
                entity.polyline.positions = new Cesium.CallbackProperty(() => {
                    const s = _poseCache.get(srcId);
                    const d = _poseCache.get(dstId);
                    return [s ? s.position : p1, d ? d.position : p2];
                }, false) as any;
                // Only recreate material if color actually changed
                const currentMat = entity.polyline.material as any;
                const currentColor = currentMat?.color?.getValue?.(Cesium.JulianDate.now());
                if (!currentColor || !Cesium.Color.equals(currentColor, lineColor)) {
                    entity.polyline.material = new Cesium.PolylineGlowMaterialProperty({
                        glowPower: 0.2,
                        color: lineColor,
                    });
                }
            } else if (!entity) {
                const srcId = elementId;
                const dstId = peerElementId;
                viewer.entities.add({
                    id: commId,
                    polyline: {
                        positions: new Cesium.CallbackProperty(() => {
                            const s = _poseCache.get(srcId);
                            const d = _poseCache.get(dstId);
                            return [s ? s.position : p1, d ? d.position : p2];
                        }, false) as any,
                        width: 2,
                        material: new Cesium.PolylineGlowMaterialProperty({
                            glowPower: 0.2,
                            color: lineColor,
                        }),
                        arcType: bothGround ? Cesium.ArcType.GEODESIC : Cesium.ArcType.NONE,
                    },
                });
            }
        }
    }

    // Remove stale comm lines
    const toRemove: string[] = [];
    viewer.entities.values.forEach(e => {
        if (e.id.startsWith('peer-comm-') && !activePairs.has(e.id)) {
            toRemove.push(e.id);
        }
    });
    toRemove.forEach(id => viewer.entities.removeById(id));
};

// ═══════════════════════════════════════════════════════════════════════
// § 14 — FOV-BASED COMM LINES (Satellite–Ground)
// ═══════════════════════════════════════════════════════════════════════

export const updateFovCommLines = (
    viewer: Cesium.Viewer,
    elementMap: ElementMap,
    visSettings: VisualizationSettings,
): void => {
    const satellites = Object.values(elementMap).filter(e => e.elementType === 'satellite');
    const groundStations = Object.values(elementMap).filter(e => e.elementType === 'groundstation');

    const activeFovComms = new Set<string>();

    for (const sat of satellites) {
        // Read position from _poseCache (works for both live + replay)
        const satCache = _poseCache.get(sat.elementId);
        if (!satCache) continue;
        const satPos = satCache.position;
        if (Cesium.Cartesian3.magnitude(satPos) < 1000) continue;

        if (!isFeatureVisible(visSettings.commLines, sat.elementId)) continue;
        if (!isFeatureVisible(visSettings.elementVisibility, sat.elementId)) continue;

        for (const gs of groundStations) {
            const gsCache = _poseCache.get(gs.elementId);
            if (!gsCache) continue;
            const gsPos = gsCache.position;
            if (Cesium.Cartesian3.magnitude(gsPos) < 1000) continue;

            if (!isFeatureVisible(visSettings.commLines, gs.elementId)) continue;
            if (!isFeatureVisible(visSettings.elementVisibility, gs.elementId)) continue;

            const elevationRad = calculateElevationAngle(gsPos, satPos);
            const elevationDeg = Cesium.Math.toDegrees(elevationRad);

            if (elevationDeg >= MIN_ELEVATION_DEG) {
                const commId = `comm-${sat.elementId}-${gs.elementId}`;
                activeFovComms.add(commId);

                // Skip connections that are occluded by Earth
                if (doesLineIntersectEarth(satPos, gsPos)) continue;
                const lineColor = Cesium.Color.YELLOW;

                const satId = sat.elementId;
                const gsId = gs.elementId;
                const entity = viewer.entities.getById(commId);
                if (entity && entity.polyline) {
                    // Positions via CallbackProperty for live tracking
                    if (!(entity.polyline.positions instanceof Cesium.CallbackProperty)) {
                        entity.polyline.positions = new Cesium.CallbackProperty(() => {
                            const s = _poseCache.get(satId);
                            const g = _poseCache.get(gsId);
                            return [g ? g.position : gsPos, s ? s.position : satPos];
                        }, false) as any;
                    }
                    // Update color
                    const currentMat = entity.polyline.material as any;
                    const currentColor = currentMat?.color?.getValue?.(Cesium.JulianDate.now());
                    if (!currentColor || !Cesium.Color.equals(currentColor, lineColor)) {
                        entity.polyline.material = new Cesium.PolylineGlowMaterialProperty({
                            glowPower: 0.2,
                            color: lineColor,
                        });
                    }
                } else if (!entity) {
                    viewer.entities.add({
                        id: commId,
                        polyline: {
                            positions: new Cesium.CallbackProperty(() => {
                                const s = _poseCache.get(satId);
                                const g = _poseCache.get(gsId);
                                return [g ? g.position : gsPos, s ? s.position : satPos];
                            }, false) as any,
                            width: 2,
                            material: new Cesium.PolylineGlowMaterialProperty({
                                glowPower: 0.2,
                                color: lineColor,
                            }),
                            arcType: Cesium.ArcType.NONE,
                        },
                    });
                }
            }
        }
    }

    // Remove stale FOV comm lines
    const toRemove: string[] = [];
    viewer.entities.values.forEach(e => {
        if (e.id.startsWith('comm-') && !e.id.startsWith('peer-comm-') && !activeFovComms.has(e.id)) {
            toRemove.push(e.id);
        }
    });
    toRemove.forEach(id => viewer.entities.removeById(id));
};

/**
 * Update geometric comm lines — works in both live & replay mode.
 * Draws lines between all element pairs (excluding missiles) where
 * the line-of-sight is valid. Uses _poseCache positions directly.
 * For satellite↔ground: requires elevation ≥ 10°.
 * For satellite↔satellite: requires clear line-of-sight.
 * Color: YELLOW (clear) / RED (Earth-occluded).
 */
export const updateGeometricCommLines = (
    viewer: Cesium.Viewer,
    elementMap: ElementMap,
    visSettings: VisualizationSettings,
): void => {
    const elements = Object.values(elementMap).filter(
        e => e.elementType !== 'missile' && e.elementType !== 'unknown',
    );
    const activeComms = new Set<string>();

    for (let i = 0; i < elements.length; i++) {
        const elA = elements[i];
        const cacheA = _poseCache.get(elA.elementId);
        if (!cacheA) continue;
        if (Cesium.Cartesian3.magnitude(cacheA.position) < 1000) continue;
        if (!isFeatureVisible(visSettings.commLines, elA.elementId)) continue;
        if (!isFeatureVisible(visSettings.elementVisibility, elA.elementId)) continue;

        for (let j = i + 1; j < elements.length; j++) {
            const elB = elements[j];
            const cacheB = _poseCache.get(elB.elementId);
            if (!cacheB) continue;
            if (Cesium.Cartesian3.magnitude(cacheB.position) < 1000) continue;
            if (!isFeatureVisible(visSettings.commLines, elB.elementId)) continue;
            if (!isFeatureVisible(visSettings.elementVisibility, elB.elementId)) continue;

            const typeA = elA.elementType;
            const typeB = elB.elementType;
            const posA = cacheA.position;
            const posB = cacheB.position;

            // Determine if connection is valid
            let isValid = false;

            if (
                (typeA === 'satellite' && typeB === 'groundstation') ||
                (typeA === 'groundstation' && typeB === 'satellite')
            ) {
                // Satellite↔Ground: check elevation angle ≥ 10°
                const gsPos = typeA === 'groundstation' ? posA : posB;
                const satPos = typeA === 'satellite' ? posA : posB;
                const elevationRad = calculateElevationAngle(gsPos, satPos);
                const elevationDeg = Cesium.Math.toDegrees(elevationRad);
                isValid = elevationDeg >= MIN_ELEVATION_DEG;
            } else if (typeA === 'satellite' && typeB === 'satellite') {
                // Satellite↔Satellite: check line-of-sight (no Earth occlusion)
                isValid = !doesLineIntersectEarth(posA, posB);
            } else if (typeA === 'groundstation' && typeB === 'groundstation') {
                // Ground↔Ground: always connected if both visible
                isValid = true;
            } else {
                // Other pairs (aircraft, vessel, etc): line-of-sight
                isValid = !doesLineIntersectEarth(posA, posB);
            }

            if (!isValid) continue;

            // Sorted IDs to prevent duplicates
            const sortedIds = [elA.elementId, elB.elementId].sort();
            const commId = `comm-${sortedIds[0]}-${sortedIds[1]}`;
            activeComms.add(commId);

            // Skip connections that are occluded by Earth
            if (doesLineIntersectEarth(posA, posB)) continue;
            const lineColor = Cesium.Color.YELLOW;

            const bothGround = typeA === 'groundstation' && typeB === 'groundstation';

            // Elevate ground-to-ground slightly to avoid z-fighting
            let fallbackA = posA, fallbackB = posB;
            if (bothGround) {
                const u1 = Cesium.Cartesian3.normalize(posA, new Cesium.Cartesian3());
                fallbackA = Cesium.Cartesian3.add(posA, u1, new Cesium.Cartesian3());
                const u2 = Cesium.Cartesian3.normalize(posB, new Cesium.Cartesian3());
                fallbackB = Cesium.Cartesian3.add(posB, u2, new Cesium.Cartesian3());
            }

            const idA = elA.elementId;
            const idB = elB.elementId;
            const entity = viewer.entities.getById(commId);
            if (entity && entity.polyline) {
                // Update color if changed
                const currentMat = entity.polyline.material as any;
                const currentColor = currentMat?.color?.getValue?.(Cesium.JulianDate.now());
                if (!currentColor || !Cesium.Color.equals(currentColor, lineColor)) {
                    entity.polyline.material = new Cesium.PolylineGlowMaterialProperty({
                        glowPower: 0.2,
                        color: lineColor,
                    });
                }
            } else if (!entity) {
                viewer.entities.add({
                    id: commId,
                    polyline: {
                        positions: new Cesium.CallbackProperty(() => {
                            const cA = _poseCache.get(idA);
                            const cB = _poseCache.get(idB);
                            return [cA ? cA.position : fallbackA, cB ? cB.position : fallbackB];
                        }, false) as any,
                        width: 2,
                        material: new Cesium.PolylineGlowMaterialProperty({
                            glowPower: 0.2,
                            color: lineColor,
                        }),
                        arcType: bothGround ? Cesium.ArcType.GEODESIC : Cesium.ArcType.NONE,
                    },
                });
            }
        }
    }

    // Remove stale comm lines (both comm- and peer-comm-)
    const toRemove: string[] = [];
    viewer.entities.values.forEach(e => {
        if (e.id.startsWith('comm-') && !activeComms.has(e.id)) {
            toRemove.push(e.id);
        }
    });
    toRemove.forEach(id => viewer.entities.removeById(id));
};

// ═══════════════════════════════════════════════════════════════════════
// § 18 — DERIVED DISPLAY VALUES
// ═══════════════════════════════════════════════════════════════════════

export const computeAltitudeKm = (pos: { x: number; y: number; z: number }): number => {
    const rMag = Math.sqrt(pos.x ** 2 + pos.y ** 2 + pos.z ** 2);
    return (rMag - EARTH_RADIUS) / 1000;
};

export const computeSpeedMs = (vel: { x: number; y: number; z: number }): number =>
    Math.sqrt(vel.x ** 2 + vel.y ** 2 + vel.z ** 2);

// ═══════════════════════════════════════════════════════════════════════
// § 19 — LVLH EULER ANGLES
// ═══════════════════════════════════════════════════════════════════════

export const computeLVLHEulerAngles = (
    pos: { x: number; y: number; z: number },
    vel: { x: number; y: number; z: number },
    q: { q1: number; q2: number; q3: number; q4: number },
): { roll: number; pitch: number; yaw: number } | null => {
    // Guard: LVLH frame requires non-zero position AND velocity.
    // Ground stations (vel ≈ 0) cannot define an along-track axis.
    const posMag = Math.sqrt(pos.x ** 2 + pos.y ** 2 + pos.z ** 2);
    const velMag = Math.sqrt(vel.x ** 2 + vel.y ** 2 + vel.z ** 2);
    if (posMag < 1e-6 || velMag < 1e-3) return null;

    const r = new Cesium.Cartesian3(pos.x, pos.y, pos.z);
    const v = new Cesium.Cartesian3(vel.x, vel.y, vel.z);

    // LVLH frame axes
    const zLVLH = Cesium.Cartesian3.negate(
        Cesium.Cartesian3.normalize(r, new Cesium.Cartesian3()),
        new Cesium.Cartesian3()
    ); // Nadir

    const h = Cesium.Cartesian3.cross(r, v, new Cesium.Cartesian3());
    const yLVLH = Cesium.Cartesian3.negate(
        Cesium.Cartesian3.normalize(h, new Cesium.Cartesian3()),
        new Cesium.Cartesian3()
    ); // -orbit normal

    const xLVLH = Cesium.Cartesian3.cross(yLVLH, zLVLH, new Cesium.Cartesian3());
    Cesium.Cartesian3.normalize(xLVLH, xLVLH); // Along-track

    // LVLH→ECEF rotation (columns = LVLH axes)
    const lvlhToEcef = new Cesium.Matrix3(
        xLVLH.x, yLVLH.x, zLVLH.x,
        xLVLH.y, yLVLH.y, zLVLH.y,
        xLVLH.z, yLVLH.z, zLVLH.z,
    );

    // Transpose → ECEF to LVLH
    const ecefToLvlh = Cesium.Matrix3.transpose(lvlhToEcef, new Cesium.Matrix3());

    // Body-to-ECEF from quaternion
    const quat = new Cesium.Quaternion(q.q1, q.q2, q.q3, q.q4);
    const bodyToEcef = Cesium.Matrix3.fromQuaternion(quat);

    // Body-to-LVLH = ecefToLvlh × bodyToEcef
    const bodyToLvlh = Cesium.Matrix3.multiply(ecefToLvlh, bodyToEcef, new Cesium.Matrix3());

    // Extract ZYX Euler angles
    const R = Cesium.Matrix3.toArray(bodyToLvlh); // column-major!
    // Access as row-major: R[row*3+col] for column-major → R[col*3+row]
    const r00 = R[0], r10 = R[1], r20 = R[2];
    const r01 = R[3], r11 = R[4]; // r21 = R[5]
    const _r02 = R[6]; // eslint-disable-line
    const r21 = R[5], r22 = R[8];

    const pitch = Math.asin(-Math.max(-1, Math.min(1, r20)));
    let roll: number, yaw: number;

    if (Math.abs(Math.cos(pitch)) > 1e-6) {
        roll = Math.atan2(r21, r22);
        yaw = Math.atan2(r10, r00);
    } else {
        roll = 0;
        yaw = Math.atan2(-r01, r11);
    }

    const toDeg = 180 / Math.PI;
    return { roll: roll * toDeg, pitch: pitch * toDeg, yaw: yaw * toDeg };
};

// ═══════════════════════════════════════════════════════════════════════
// VISIBILITY HELPER
// ═══════════════════════════════════════════════════════════════════════

export const isFeatureVisible = (
    settings: { globalEnabled: boolean; perElement: Record<string, boolean> },
    elementId: string,
): boolean => {
    const perEl = settings.perElement[elementId];
    // If no per-element override, fall back to globalEnabled (the default for the feature)
    return perEl === undefined ? settings.globalEnabled : perEl;
};

// ═══════════════════════════════════════════════════════════════════════
// MAIN ENTITY UPDATE — updateEntityFromECEF
// ═══════════════════════════════════════════════════════════════════════

/**
 * Main entry point: processes a RepElementFixedState message.
 * Creates/updates the entity and all associated visuals.
 */
export const updateEntityFromECEF = (
    viewer: Cesium.Viewer,
    data: ElementPositionData,
    elementMap: ElementMap,
    visSettings: VisualizationSettings,
): void => {
    const { elementId, elementName, position, velocity, quaternion, timestamp } = data;
    const cartesian = ecefToCartesian3(position.x, position.y, position.z);
    const elType = resolveElementType(data, elementMap);

    // Element visibility check — hide element AND all associated visuals
    if (!isFeatureVisible(visSettings.elementVisibility, elementId)) {
        const entity = viewer.entities.getById(elementId);
        if (entity) entity.show = false;
        // Hide orbit track
        viewer.entities.removeById(`${elementId}-orbit`);
        _lastOrbitH.delete(elementId);
        // Hide FOV cone
        viewer.entities.removeById(`${elementId}-fov`);
        // Hide FOR cone
        viewer.entities.removeById(`${elementId}-for`);
        // Hide orientation axes
        removeOrientationAxes(viewer, elementId);
        // Hide trajectory trail
        const trailEntity = viewer.entities.getById(`${elementId}-trail`);
        if (trailEntity) trailEntity.show = false;
        return;
    }

    // Orientation
    const orientation = calculateModelOrientation(data, elType);

    // ── Update pose cache BEFORE creating/updating cones ──
    // FOV/FOR use CallbackProperty that reads from this cache every frame,
    // so they are always in lock-step with the parent element.
    _poseCache.set(elementId, { position: cartesian, orientation, elementType: elType });

    // ── Create or Update Entity ──
    let entity = viewer.entities.getById(elementId);
    if (entity) {
        entity.show = true;
        // No need to reassign position/orientation — the entity's CallbackProperty
        // reads from _poseCache which was already updated above.
        // Rotate missile triangle billboard to match heading
        if (elType === 'missile' && entity.billboard) {
            const vel = new Cesium.Cartesian3(velocity.x, velocity.y, velocity.z);
            const heading = computeHeadingFromVelocity(cartesian, vel);
            (entity.billboard.rotation as any) = new Cesium.ConstantProperty(-heading);
        }
    } else {
        if (elType === 'missile') {
            // Missile: red triangle billboard with heading rotation
            const vel = new Cesium.Cartesian3(velocity.x, velocity.y, velocity.z);
            const heading = computeHeadingFromVelocity(cartesian, vel);

            entity = viewer.entities.add({
                id: elementId,
                name: elementName,
                position: new Cesium.CallbackProperty(() => {
                    const c = _poseCache.get(elementId);
                    return c ? c.position : cartesian;
                }, false) as any,
                orientation: new Cesium.CallbackProperty(() => {
                    const c = _poseCache.get(elementId);
                    return c ? c.orientation : orientation;
                }, false) as any,
                billboard: {
                    image: getMissileTriangleImage() as any,
                    width: 12,
                    height: 12,
                    rotation: -heading,
                    alignedAxis: Cesium.Cartesian3.ZERO, // always face camera
                    verticalOrigin: Cesium.VerticalOrigin.CENTER,
                    horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
                },
                label: {
                    text: truncateName(elementName, 12, 6, 6),
                    font: '12px Open Sans',
                    fillColor: Cesium.Color.WHITE,
                    outlineColor: Cesium.Color.BLACK,
                    outlineWidth: 2,
                    style: Cesium.LabelStyle.FILL_AND_OUTLINE,
                    pixelOffset: new Cesium.Cartesian2(0, -16),
                    showBackground: true,
                    backgroundColor: Cesium.Color.BLACK.withAlpha(0.6),
                    backgroundPadding: new Cesium.Cartesian2(6, 4),
                    horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
                    verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
                },
            });
        } else {
        // Determine point color
        const pointColor = elType === 'satellite'
                ? Cesium.Color.fromCssColorString(SAT_COLOR)
                : Cesium.Color.DODGERBLUE;

        entity = viewer.entities.add({
            id: elementId,
            name: elementName,
            position: new Cesium.CallbackProperty(() => {
                const c = _poseCache.get(elementId);
                return c ? c.position : cartesian;
            }, false) as any,
            orientation: new Cesium.CallbackProperty(() => {
                const c = _poseCache.get(elementId);
                return c ? c.orientation : orientation;
            }, false) as any,
            point: {
                pixelSize: 10,
                color: pointColor,
                outlineColor: Cesium.Color.WHITE,
                outlineWidth: 2,
            },
            label: {
                text: truncateName(elementName, 12, 6, 6),
                font: '12px Open Sans',
                fillColor: Cesium.Color.WHITE,
                outlineColor: Cesium.Color.BLACK,
                outlineWidth: 2,
                style: Cesium.LabelStyle.FILL_AND_OUTLINE,
                pixelOffset: new Cesium.Cartesian2(0, -12),
                showBackground: true,
                backgroundColor: Cesium.Color.BLACK.withAlpha(0.6),
                backgroundPadding: new Cesium.Cartesian2(6, 4),
                horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
                verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
            },
        });
        }
    }

    // ── Orbital Track (satellites only, computed from r × v) ──
    // Recalculates when the angular-momentum vector (h = r × v) changes
    // beyond a threshold, indicating a change in the orbital plane.
    const altitudeForOrbit = Math.sqrt(position.x ** 2 + position.y ** 2 + position.z ** 2) - EARTH_RADIUS;
    if (elType === 'satellite' && isFeatureVisible(visSettings.orbitTracks, elementId) && altitudeForOrbit > 100_000) {
        const orbitId = `${elementId}-orbit`;
        if (isGEO(position)) {
            // GEO satellites don't get orbit tracks
        } else {
            const velMag = Math.sqrt(velocity.x ** 2 + velocity.y ** 2 + velocity.z ** 2);
            if (velMag > 0.1) {
                // Compute current angular-momentum unit vector: ĥ = (r × v) / |r × v|
                const hx = position.y * velocity.z - position.z * velocity.y;
                const hy = position.z * velocity.x - position.x * velocity.z;
                const hz = position.x * velocity.y - position.y * velocity.x;
                const hMag = Math.sqrt(hx ** 2 + hy ** 2 + hz ** 2);

                let needsRecalc = false;
                const existingOrbit = viewer.entities.getById(orbitId);

                if (!existingOrbit) {
                    needsRecalc = true; // first time
                } else if (hMag > 1e-10) {
                    // Compare normalised h-vector with last stored value
                    const hNorm = { hx: hx / hMag, hy: hy / hMag, hz: hz / hMag };
                    const prev = _lastOrbitH.get(elementId);
                    if (prev) {
                        // Use cross-product magnitude as angular difference metric
                        const cx = prev.hy * hNorm.hz - prev.hz * hNorm.hy;
                        const cy = prev.hz * hNorm.hx - prev.hx * hNorm.hz;
                        const cz = prev.hx * hNorm.hy - prev.hy * hNorm.hx;
                        const crossMag = Math.sqrt(cx ** 2 + cy ** 2 + cz ** 2);
                        if (crossMag > ORBIT_H_CHANGE_THRESHOLD) {
                            needsRecalc = true;
                        }
                    } else {
                        needsRecalc = true; // no previous record
                    }
                }

                if (needsRecalc) {
                    const orbitPts = generateOrbitalTrackFromStateVector(position, velocity);
                    if (orbitPts && orbitPts.length > 0) {
                        // Remove old orbit entity and add fresh one
                        viewer.entities.removeById(orbitId);
                        viewer.entities.add({
                            id: orbitId,
                            name: `${elementName} Orbit`,
                            polyline: {
                                positions: orbitPts,
                                width: 1,
                                material: Cesium.Color.fromCssColorString('#E6A64E'),
                                arcType: Cesium.ArcType.NONE,
                            },
                        });
                        // Store normalised h-vector
                        if (hMag > 1e-10) {
                            _lastOrbitH.set(elementId, { hx: hx / hMag, hy: hy / hMag, hz: hz / hMag });
                        }
                    }
                }
            }
            // else: velocity too small, will retry on next message
        }
    } else {
        // Remove orbit if toggle turned off, element is not a satellite, or suborbital
        viewer.entities.removeById(`${elementId}-orbit`);
        _lastOrbitH.delete(elementId);
    }

    // ── FOV Cone ──
    if (isFeatureVisible(visSettings.fovCones, elementId) && (elType === 'satellite' || elType === 'groundstation')) {
        const fovId = `${elementId}-fov`;
        if (!viewer.entities.getById(fovId)) {
            createFOVCone(viewer, elementId, elementName, cartesian, orientation, elType);
        } else {
            updateFOVCone(viewer, elementId, cartesian, orientation, elType);
        }
    } else {
        viewer.entities.removeById(`${elementId}-fov`);
    }

    // ── FOR Cone ──
    if (isFeatureVisible(visSettings.forCones, elementId) && (elType === 'satellite' || elType === 'groundstation')) {
        const forId = `${elementId}-for`;
        if (!viewer.entities.getById(forId)) {
            createFORCone(viewer, elementId, elementName, cartesian, elType);
        } else {
            updateFORCone(viewer, elementId, cartesian, elType);
        }
    } else {
        viewer.entities.removeById(`${elementId}-for`);
    }

    // ── Orientation Axes ──
    if (isFeatureVisible(visSettings.orientationAxes, elementId) && (elType === 'satellite' || TRAJECTORY_TYPES.includes(elType))) {
        updateOrientationAxes(viewer, elementId, cartesian, orientation, elType);
    } else {
        removeOrientationAxes(viewer, elementId);
    }

    // ── Trajectory Trail (missile/aircraft/vessel — NOT satellites) ──
    if (TRAJECTORY_TYPES.includes(elType)) {
        const newPoint: TrailPoint = { x: position.x, y: position.y, z: position.z, timestamp };

        // Append to module-level cache (no React state update needed)
        const existing = _trailDataCache.get(elementId) || [];
        existing.push(newPoint);
        _trailDataCache.set(elementId, existing);

        const trailId = `${elementId}-trail`;
        const shouldShow = isFeatureVisible(visSettings.trajectoryTrails, elementId)
            && isFeatureVisible(visSettings.elementVisibility, elementId)
            && existing.length >= 2;

        if (shouldShow) {
            updateTrajectoryTrail(viewer, elementId, elementName, elType, existing);
        } else {
            const trailEntity = viewer.entities.getById(trailId);
            if (trailEntity) trailEntity.show = false;
        }
    }

    throttledRequestRender(viewer);
};

// ═══════════════════════════════════════════════════════════════════════
// HANDLE WS MESSAGES
// ═══════════════════════════════════════════════════════════════════════

export const handleRepElementFixedState = (
    viewer: Cesium.Viewer,
    msg: WSMessage,
    elementMap: ElementMap,
    visSettings: VisualizationSettings,
): void => {
    const posData = parsePositionFromPayload(msg);
    if (!posData) return;
    updateEntityFromECEF(viewer, posData, elementMap, visSettings);
};

// ═══════════════════════════════════════════════════════════════════════
// CLEANUP
// ═══════════════════════════════════════════════════════════════════════

/** Append an ECEF state to the module-level history cache. */
export const appendEcefHistory = (elementId: string, state: ECEFState): void => {
    const existing = _ecefHistoryCache.get(elementId) || [];
    existing.push(state);
    _ecefHistoryCache.set(elementId, existing);
};

/** Read the ECEF history for all elements. */
export const getEcefHistorySnapshot = (): Record<string, ECEFState[]> => {
    const out: Record<string, ECEFState[]> = {};
    _ecefHistoryCache.forEach((v, k) => { out[k] = v; });
    return out;
};

export const cleanupCesiumPlot = (viewer: Cesium.Viewer | null): void => {
    if (!isViewerReady(viewer)) return;
    viewer.entities.removeAll();
    _trailCache.clear();
    _trailDataCache.clear();
    _ecefHistoryCache.clear();
    _lastOrbitH.clear();
    _poseCache.clear();
    _fovFrameCache.clear();
    _forFrameCache.clear();
    viewer.scene.requestRender();
};

// ═══════════════════════════════════════════════════════════════════════
// DEFAULT VISUALIZATION SETTINGS
// ═══════════════════════════════════════════════════════════════════════

export const DEFAULT_VISUALIZATION_SETTINGS: VisualizationSettings = {
    elementVisibility: { globalEnabled: true, perElement: {} },
    orbitTracks: { globalEnabled: true, perElement: {} },
    fovCones: { globalEnabled: false, perElement: {} },
    forCones: { globalEnabled: false, perElement: {} },
    trajectoryTrails: { globalEnabled: true, perElement: {} },
    orientationAxes: { globalEnabled: false, perElement: {} },
    commLines: { globalEnabled: false, perElement: {} },
};
